/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import Footer from "../components/Footer";

const MSMENews = () => {
  const [msmeNews, setMsmeNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_NEWS = process.env.REACT_APP_API_BASE_URL + "/UInews";
  const API_MEDIA = process.env.REACT_APP_API_BASE_URL + "/UImedia";

  // Fetch news from API
  const fetchNews = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_NEWS);
      let newsData = res.data.results || res.data.data || res.data || [];

      // If newsData is not an array, convert it to array
      if (!Array.isArray(newsData)) {
        newsData = [newsData];
      }

      // Load images for each news item
      const newsWithImages = await Promise.all(
        newsData.map(async (newsItem) => {
          if (newsItem.image?.id) {
            try {
              const imgRes = await axios.get(`${API_MEDIA}/${newsItem.image.id}/download`, {
                responseType: "blob",
              });
              newsItem.imageUrl = URL.createObjectURL(imgRes.data);
            } catch (err) {
              console.error("Error loading image for", newsItem.title, err);
              newsItem.imageUrl = "https://via.placeholder.com/400x200.png?text=No+Image";
            }
          } else {
            newsItem.imageUrl = "https://via.placeholder.com/400x200.png?text=No+Image";
          }
          return newsItem;
        })
      );

      // Filter only active news and sort by date (newest first)
      const activeNews = newsWithImages
        .filter(newsItem => newsItem.isActive !== false)
        .sort((a, b) => new Date(b.date) - new Date(a.date));

      setMsmeNews(activeNews);
    } catch (err) {
      console.error("Error fetching news:", err);
      setError("Failed to load news. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown Date';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        // If it's already a formatted string, return as is
        return dateString;
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white font-sans pt-24 px-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-lg text-gray-300">Loading latest news...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white font-sans pt-24 px-6 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-white mb-2">Oops! Something went wrong</h2>
          <p className="text-gray-300 mb-4">{error}</p>
          <button
            onClick={fetchNews}
            className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-3 rounded-lg font-semibold transition-colors duration-300"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white font-sans pt-24 px-6 flex flex-col justify-between">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="text-center mb-10"
      >
        <h1 className="text-4xl md:text-5xl font-bold">Latest MSME News</h1>
        <div className="h-1 w-24 bg-yellow-500 mx-auto mt-3 rounded-full"></div>
        <div className="mt-4 text-yellow-400">
          {msmeNews.length} {msmeNews.length === 1 ? 'article' : 'articles'} available
        </div>
      </motion.div>

      {/* News Grid */}
      {msmeNews.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">📰</div>
            <h2 className="text-2xl font-bold text-white mb-2">No News Available</h2>
            <p className="text-gray-300">Check back later for the latest MSME news and updates.</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-20">
          {msmeNews.map((news, index) => (
            <motion.div
              key={news.id || news._id}
              className="bg-[#121212] border border-gray-700 shadow-lg rounded-2xl overflow-hidden hover:shadow-xl transition-all"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              whileHover={{ scale: 1.03 }}
            >
              <div className="relative">
                <img
                  src={news.imageUrl}
                  alt={news.title}
                  className="w-full h-48 object-cover rounded-t-2xl"
                  onError={(e) => (e.target.src = "https://via.placeholder.com/300x200?text=Image+Not+Found")}
                />
                {index === 0 && (
                  <span className="absolute top-3 left-3 bg-yellow-500 text-black text-xs font-bold px-2 py-1 rounded-full">
                    Latest
                  </span>
                )}
                {news.userId?.name && (
                  <span className="absolute top-3 right-3 bg-gray-800 text-white text-xs font-medium px-2 py-1 rounded-full">
                    {news.userId.name}
                  </span>
                )}
              </div>
              <div className="p-5">
                <p className="text-sm text-gray-400">{formatDate(news.date)}</p>
                <h2 className="text-lg font-semibold mt-2 line-clamp-2">{news.title}</h2>
                <p className="text-gray-400 mt-2 text-sm line-clamp-3">
                  {news.description || 'Click "Read More" to view the full article.'}
                </p>
                <div className="mt-4 text-right">
                  <a
                    href={news.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-yellow-500 text-black py-2 px-4 rounded-lg font-semibold hover:bg-yellow-400 transition-all hover:scale-105"
                  >
                    Read More →
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <Footer />
    </div>
  );
};

export default MSMENews;




//old code for reference


// import React from "react";
// import { motion } from "framer-motion";
// import Footer from "../components/Footer";

// const msmeNews = [
//   {
//     title: "India's Market Regulator Tightens Rules for IPOs of Small Firms",
//     date: "March 21, 2025",
//     image: "https://res.cloudinary.com/dnmlijyox/image/upload/MSME-news-imgs/sebi.webp",
//     description: "SEBI introduces stricter guidelines for SME IPOs to ensure transparency and investor protection.",
//     link: "https://www.reuters.com/markets/asia/indias-market-regulator-tightens-rules-ipos-small-firms-2024-12-18/",
//   },
//   {
//     title: "Piyush Goyal Emphasizes MSME Sector's Key Role in Driving Services Exports and Creating Jobs",
//     date: "March 21, 2025",
//   image: "https://res.cloudinary.com/dnmlijyox/image/upload/MSME-news-imgs/goyal.webp",
//     description: "Commerce Minister highlights MSMEs as a crucial force in India's export and employment sectors.",
//     link: "https://www.business-standard.com/markets/capital-market-news/piyush-goyal-emphasizes-msme-sector-s-key-role-in-driving-services-exports-and-creating-jobs-125032100483_1.html",
//   },
//   {
//     title: "ET MSME Awards 2024: Meet the 20 Winners of India's Biggest MSME Awards",
//     date: "March 21, 2025",
//   image: "https://res.cloudinary.com/dnmlijyox/image/upload/MSME-news-imgs/msme-awards.webp",
//     description: "Recognizing outstanding MSMEs that are shaping India's business landscape.",
//     link: "https://m.economictimes.com/small-biz/et-msme-awards-2024-meet-the-20-winners-of-indias-biggest-msme-awards/articleshow/119287122.cms",
//   },
// ];

// const MSMENews = () => {
//   return (
//     <div className="min-h-screen bg-[#0a0a0a] text-white font-sans pt-24 px-6 flex flex-col justify-between">
//       {/* Page Header */}
//       <motion.div
//         initial={{ opacity: 0, y: -20 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ duration: 0.6, ease: "easeOut" }}
//         className="text-center mb-10"
//       >
//         <h1 className="text-4xl md:text-5xl font-bold">Latest MSME News</h1>
//         <div className="h-1 w-24 bg-yellow-500 mx-auto mt-3 rounded-full"></div>
//       </motion.div>

//       {/* News Grid */}
//       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-20">
//         {msmeNews.map((news, index) => (
//           <motion.div
//             key={index}
//             className="bg-[#121212] border border-gray-700 shadow-lg rounded-2xl overflow-hidden hover:shadow-xl transition-all"
//             initial={{ opacity: 0, y: 20 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.6, delay: index * 0.2 }}
//             whileHover={{ scale: 1.03 }}
//           >
//             <div className="relative">
//               <img
//                 src={news.image}
//                 alt={news.title}
//                 className="w-full h-48 object-cover rounded-t-2xl"
//                 onError={(e) => (e.target.src = "https://via.placeholder.com/300x200?text=Image+Not+Found")}
//               />
//               {index === 0 && (
//                 <span className="absolute top-3 left-3 bg-yellow-500 text-black text-xs font-bold px-2 py-1 rounded-full">
//                   Latest
//                 </span>
//               )}
//             </div>
//             <div className="p-5">
//               <p className="text-sm text-gray-400">{news.date}</p>
//               <h2 className="text-lg font-semibold mt-2 line-clamp-2">{news.title}</h2>
//               <p className="text-gray-400 mt-2 text-sm line-clamp-3">{news.description}</p>
//               <div className="mt-4 text-right">
//                 <a
//                   href={news.link}
//                   target="_blank"
//                   rel="noopener noreferrer"
//                   className="inline-block bg-yellow-500 text-black py-2 px-4 rounded-lg font-semibold hover:bg-yellow-400 transition-all hover:scale-105"
//                 >
//                   Read More →
//                 </a>
//               </div>
//             </div>
//           </motion.div>
//         ))}
//       </div>

//       <Footer />
//     </div>
//   );
// };

// export default MSMENews;
